import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/appShell";
import { TEAM_INFO } from "../lib/teamMap";
import { Loader } from "../components/Loader";
import { TeamBadge } from "../components/TeamBadge";
import { ChevronLeft, Star, ChevronDown, ClipboardList, MapPin, Users, ExternalLink, Flame, Snowflake, Zap, Target, Activity } from "lucide-react";
import { fmtSvFromCounts as fmtSv } from "../lib/statColumns";
import { formatDate } from "../lib/date";
import { LineupCard } from "../components/LineupCard";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { useAuth } from "../lib/AuthContext";
import { useAdminMode } from "../lib/AdminModeContext";
import { Wrench } from "lucide-react";
import PWHLGameCentre from "./PWHLGameCentre";

const STRENGTH_LABEL = { EV: "", PP: "PP", SH: "SH", EN: "EN", PS: "PS" };

// Transform a manual Rosterly box score (+ per-game logs) into the unified
// PWHLGameCentre data shape so every game renders with the same modern layout.
function manualToGameData(s, playerMap, skaterLogs, goalieLogs) {
    const person = (id) => {
        if (!id) return null;
        const pm = playerMap[id] || {};
        return { pwhl_id: id, name: pm.name || "Unknown", jersey: pm.jersey || "", position: pm.position || "" };
    };
    const people = [];
    const seen = new Set();
    const addPerson = (id, teamId) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        const pm = playerMap[id] || {};
        people.push({ pwhl_id: id, name: pm.name || "Unknown", jersey: pm.jersey || "",
            position: pm.position || "", team_id: pm.team_id || teamId,
            matched_player_id: id, match_method: "manual", candidates: [] });
    };
    const goals = (s.scoring || []).filter((g) => g.scorer_id).map((g) => {
        addPerson(g.scorer_id, g.team_id); addPerson(g.assist1_id, g.team_id); addPerson(g.assist2_id, g.team_id);
        return { period: g.period, time: g.time || "", team_id: g.team_id, scorer: person(g.scorer_id),
            assists: [g.assist1_id, g.assist2_id].filter(Boolean).map(person),
            strength: STRENGTH_LABEL[g.strength] ? g.strength : "EV",
            empty_net: g.strength === "EN", game_winning: !!g.gwg };
    });
    const penalties = (s.penalties || []).map((p) => {
        addPerson(p.player_id, p.team_id);
        return { period: p.period, time: p.time || "", team_id: p.team_id, player: person(p.player_id),
            infraction: p.infraction || "", minutes: p.minutes || 0 };
    });
    const goalies = (s.goalies || []).map((g) => {
        addPerson(g.player_id, g.team_id);
        return { team_id: g.team_id, player: person(g.player_id), saves: g.saves || 0,
            shots_against: g.shots_against || 0, goals_against: g.goals_against || 0,
            toi: g.toi || "", toi_seconds: 0, decision: g.decision || "", shutout: !!g.shutout };
    });
    const skaters = (skaterLogs || []).map((l) => {
        addPerson(l.player_id, l.team_id);
        const pm = playerMap[l.player_id] || {};
        return { team_id: l.team_id, player: { pwhl_id: l.player_id, name: pm.name || "Unknown",
            jersey: pm.jersey || "", position: pm.position || "" },
            g: l.g || 0, a: l.a || 0, p: (l.g || 0) + (l.a || 0), plus_minus: l.plus_minus || 0,
            pim: l.pim || 0, sog: l.sog || 0, shots: l.sh || l.sog || 0, hits: l.hits || 0,
            blocks: l.blocks || l.blk || 0, fow: l.fow || 0, foa: (l.fow || 0) + (l.fol || 0) };
    });
    const three_stars = (s.three_stars || []).filter((x) => x.player_id).map((x) => {
        addPerson(x.player_id, x.team_id);
        return { star: x.star, team_id: x.team_id, player: person(x.player_id) };
    });
    const ts = (st) => ({ shots: Number(st?.shots) || 0, hits: Number(st?.hits) || 0, pim: 0,
        fo_won: st?.fo_won || 0, fo_att: (st?.fo_won || 0) + (st?.fo_lost || 0),
        pp_goals: st?.pp_goals || 0, pp_attempts: st?.pp_attempts || 0 });
    return {
        pwhl_game_id: "", source_url: s.game_url || "",
        meta: {
            date: s.date || "", status: s.status, final: s.status === "Final",
            attendance: Number(s.attendance) || 0, venue: s.venue || "",
            start_time: "", end_time: "", duration: "", officials: [],
            home_team_id: s.home_team_id, away_team_id: s.away_team_id,
            home_team_code: "", away_team_code: "",
            home_score: s.home_score || 0, away_score: s.away_score || 0,
            overtime: !!s.overtime, shootout: !!s.shootout,
        },
        periods: s.periods || [],
        team_stats: { home: ts(s.home_team_stats), away: ts(s.away_team_stats) },
        goals, penalties, goalies, skaters, three_stars, pxp: [], people,
        season: s.season, stage: s.stage,
    };
}


const periodLabel = (n) => (n <= 3 ? `P${n}` : n === 4 ? "OT" : "SO");
const teamObj = (id) => TEAM_INFO[id] || { name: id || "—", abbreviation: id || "—", color: "#1E2233" };

// True when a lineup object actually has at least one named player anywhere.
const lineupHasPlayers = (lu) => {
    if (!lu) return false;
    const lines = [...(lu.forward_lines || []), ...(lu.d_pairs || [])];
    if (lines.some((line) => Array.isArray(line) && line.some((s) => s?.player_name))) return true;
    const flat = [
        ...(lu.goalies || []), ...(lu.reserves || []),
        ...(lu.pp1 || []), ...(lu.pp2 || []), ...(lu.pk1 || []), ...(lu.pk2 || []),
    ];
    return flat.some((s) => s?.player_name);
};

export default function GameCentre({ gameIdProp = null, embedded = false, gcView = null, onGcView = null, gcPeriod = null, onGcPeriod = null, onGcNavConfig = null }) {
    const { gameId: paramGameId } = useParams();
    const gameId = gameIdProp || paramGameId;
    const { hasAdminAccess } = useAuth();
    const { adminMode } = useAdminMode();

    const summaryQ = useQuery({
        queryKey: ["game-summary", gameId],
        queryFn: () => api.get(`/games/${gameId}/summary`).then((r) => r.data),
        retry: false,
    });
    const sumD = summaryQ.data;
    usePageTitle(sumD?.away_team_id ? `${String(sumD.away_team_id).toUpperCase()} @ ${String(sumD.home_team_id).toUpperCase()}${sumD.date ? ` · ${sumD.date}` : ""}` : "");
    const pwhlQ = useQuery({
        queryKey: ["game-pwhl-import", gameId],
        queryFn: () => api.get(`/games/${gameId}/pwhl`).then((r) => r.data),
        retry: false,
    });
    const skaterLogsQ = useQuery({
        queryKey: ["game-skater-logs", gameId],
        queryFn: () => api.get(`/skater-game-logs?game_id=${gameId}&played_only=true`).then((r) => r.data),
    });
    const goalieLogsQ = useQuery({
        queryKey: ["game-goalie-logs", gameId],
        queryFn: () => api.get(`/goalie-game-logs?game_id=${gameId}&played_only=true`).then((r) => r.data),
    });
    const playersQ = useQuery({
        queryKey: ["players-all-gc"],
        queryFn: () => api.get(`/players?include_inactive=true`).then((r) => r.data),
    });
    const storylinesQ = useQuery({
        queryKey: ["game-storylines", gameId],
        queryFn: () => api.get(`/games/${gameId}/storylines`).then((r) => r.data),
        retry: false,
    });
    const sd = summaryQ.data;
    const [showLineups, setShowLineups] = useState(false);
    const [lineupSide, setLineupSide] = useState("away");

    // Stable references — recomputed ONLY when the underlying query data changes.
    // Rebuilding these every render gave `manualData` a fresh identity, which made
    // PWHLGameCentre's navConfig churn and fire onNavConfig in a loop
    // ("Maximum update depth"), freezing the page (the Schedule "stuck" bug).
    const playerMap = React.useMemo(
        () => Object.fromEntries((playersQ.data || []).map((p) => [p.id, p])),
        [playersQ.data]
    );
    const manualData = React.useMemo(() => {
        if (!summaryQ.data) return null;
        const md = manualToGameData(summaryQ.data, playerMap, skaterLogsQ.data || [], goalieLogsQ.data || []);
        md.meta = {
            ...md.meta,
            home_score: null, away_score: null, attendance: 0,
            final: false, status: "Scheduled", overtime: false, shootout: false,
        };
        return md;
    }, [summaryQ.data, playerMap, skaterLogsQ.data, goalieLogsQ.data]);

    if (summaryQ.isLoading || pwhlQ.isLoading) return <Loader />;

    // Import-first: when a PWHL box score has been synced for this fixture,
    // render the rich Phase-2 Game Centre view (display-only, no season stats).
    const pwhlData = pwhlQ.data;
    if (pwhlData && pwhlData.pwhl_game_id) {
        return (
            <div className="space-y-8 fade-up" data-testid={`game-centre-${gameId}`}>
                <PWHLGameCentre data={pwhlData} admin={adminMode} gameId={gameId} hoistNav={!embedded} viewProp={gcView} onViewChange={onGcView} periodProp={gcPeriod} onPeriodChange={onGcPeriod} onNavConfig={onGcNavConfig} fixture={{ id: gameId, season: pwhlData.season || sd?.season, stage: pwhlData.stage || sd?.stage }}
                    backLink={!embedded ? (
                        <Link to={`/teams?tab=schedule${pwhlData?.meta?.date ? `&date=${pwhlData.meta.date}` : ""}`} className="text-[#94A3B8] hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1" data-testid="gc-back">
                            <ChevronLeft size={14} /> PWHL Schedule
                        </Link>
                    ) : null} />
            </div>
        );
    }

    if (summaryQ.isError || !summaryQ.data) {
        return (
            <div className="rounded-2xl bg-[#0E0F16] border border-white/10 shadow-xl shadow-black/40 p-10 text-center fade-up" data-testid="game-centre-not-found">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-2">Game Centre</h2>
                <p className="text-sm text-[#94A3B8] mb-4">No box score has been published for this fixture yet.</p>
                <Link to="/teams?tab=schedule" className="text-[#9D4CDD] hover:underline text-sm">← Back to PWHL Schedule</Link>
            </div>
        );
    }

    const s = summaryQ.data;
    const home = teamObj(s.home_team_id);
    const away = teamObj(s.away_team_id);
    const skaterLogMap = Object.fromEntries((skaterLogsQ.data || []).map((l) => [l.player_id, l]));
    const goalieLogMap = Object.fromEntries((goalieLogsQ.data || []).map((l) => [l.player_id, l]));
    const storyCards = storylinesQ.data?.cards || [];

    const homeWon = s.home_score > s.away_score;

    // Only show a game's lineup when one was MANUALLY entered for THIS game.
    // (No fallback to the team's current active lineup — auto-projected lines
    // shouldn't appear in game logs.)
    const awayLineup = lineupHasPlayers(s.away_lineup) ? s.away_lineup : null;
    const homeLineup = lineupHasPlayers(s.home_lineup) ? s.home_lineup : null;
    const lineupSides = [
        awayLineup && { k: "away", label: away.name, lineup: awayLineup, color: away.color },
        homeLineup && { k: "home", label: home.name, lineup: homeLineup, color: home.color },
    ].filter(Boolean);
    const activeSide = lineupSides.find((x) => x.k === lineupSide) || lineupSides[0];

    // Phase 2 — render the unified Game Centre layout for ALL games. Manual box
    // scores are transformed into the same shape the PWHL view consumes; empty
    // sections hide themselves. Games WITHOUT an official PWHL sync show as plain
    // fixtures — no score / attendance / final result (the fixture stays listed).
    // `manualData` is memoized above so its identity is stable across renders.
    return (
        <div className="space-y-8 fade-up" data-testid={`game-centre-${gameId}`}>
            <PWHLGameCentre data={manualData} admin={adminMode} gameId={gameId} hoistNav={!embedded} viewProp={gcView} onViewChange={onGcView} periodProp={gcPeriod} onPeriodChange={onGcPeriod} onNavConfig={onGcNavConfig} fixture={{ id: gameId, season: s.season, stage: s.stage }}
                backLink={!embedded ? (
                    <Link to="/teams?tab=schedule" className="text-[#94A3B8] hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1" data-testid="gc-back">
                        <ChevronLeft size={14} /> PWHL Schedule
                    </Link>
                ) : null} />
        </div>
    );

    // eslint-disable-next-line no-unreachable
    return (
        <div className="space-y-8 fade-up" data-testid={`game-centre-${gameId}`}>
            {/* Back link */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <Link to="/teams?tab=schedule" className="text-[#94A3B8] hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1" data-testid="gc-back">
                    <ChevronLeft size={14} /> PWHL Schedule
                </Link>
            </div>

            {/* ============== HERO — puck scoreboard ============== */}
            <PuckScoreboard s={s} home={home} away={away} homeWon={homeWon} />

            {/* ============== Venue / Attendance / Watch link ============== */}
            <GameInfoStrip s={s} />

            {/* ============== Storyline cards ============== */}
            {storyCards.length > 0 && <StorylineCards cards={storyCards} />}

            {/* ===== Projected Lineup — only when a lineup was entered for this game ===== */}
            {lineupSides.length > 0 && activeSide && (
            <section className="rosterly-card p-0 overflow-hidden" data-testid="gc-projected-lineup-top">
                <button
                    type="button"
                    onClick={() => setShowLineups((v) => !v)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors"
                    data-testid="gc-projected-lineup-toggle"
                    aria-expanded={showLineups}
                >
                    <span className="font-heading text-sm font-bold uppercase tracking-wider text-white inline-flex items-center gap-2">
                        <ClipboardList size={15} className="text-[#9D4CDD]" /> Lineup
                        <span className="text-[10px] text-[#64748B] font-normal normal-case tracking-normal">— line combinations &amp; special teams</span>
                    </span>
                    <ChevronDown size={18} className={`text-[#94A3B8] transition-transform ${showLineups ? "rotate-180" : ""}`} />
                </button>
                {showLineups && (
                    <div className="px-5 pb-5 space-y-4 border-t border-[#1E2233] pt-5" data-testid="gc-projected-lineup-body">
                        {lineupSides.length > 1 && (
                        <div className="flex gap-1.5" data-testid="gc-lineup-side-toggle">
                            {lineupSides.map(({ k, label }) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => setLineupSide(k)}
                                    data-testid={`gc-lineup-side-${k}`}
                                    className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${activeSide.k === k ? "bg-[#9D4CDD] text-white" : "bg-white/5 text-[#94A3B8] hover:text-white"}`}
                                >
                                    {k === "away" ? "Away" : "Home"} · {label}
                                </button>
                            ))}
                        </div>
                        )}
                        <div data-testid={`gc-lineup-${activeSide.k}-top`}>
                            <LineupCard lineup={activeSide.lineup} primary={activeSide.color} playerMap={playerMap} title={`${activeSide.label} — Lineup`} />
                        </div>
                    </div>
                )}
            </section>
            )}

            {/* ============== Period strip ============== */}
            {Array.isArray(s.periods) && s.periods.length > 0 && (
                <section className="rosterly-card p-5" data-testid="gc-period-strip">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Period-by-period</h2>
                    <div className="overflow-x-auto">
                        <table className="rosterly-table w-full">
                            <thead>
                                <tr>
                                    <th className="text-left"></th>
                                    {s.periods.map((p) => (
                                        <th key={p.period} className="text-center" data-testid={`gc-period-head-${p.period}`}>
                                            {periodLabel(p.period)}
                                        </th>
                                    ))}
                                    <th className="text-center font-bold">T</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="text-left stat-emph">{away.abbreviation}</td>
                                    {s.periods.map((p) => (
                                        <td key={p.period} className="text-center font-mono-stat" data-testid={`gc-period-away-${p.period}`}>{p.away}</td>
                                    ))}
                                    <td className="text-center font-mono-stat font-bold text-white">{s.away_score}</td>
                                </tr>
                                <tr>
                                    <td className="text-left stat-emph">{home.abbreviation}</td>
                                    {s.periods.map((p) => (
                                        <td key={p.period} className="text-center font-mono-stat" data-testid={`gc-period-home-${p.period}`}>{p.home}</td>
                                    ))}
                                    <td className="text-center font-mono-stat font-bold text-white">{s.home_score}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* ============== Goal Timeline ============== */}
            {Array.isArray(s.scoring) && s.scoring.some((p) => p.period <= 4) && (
                <GameTimeline scoring={s.scoring} home={home} away={away} homeId={s.home_team_id} awayId={s.away_team_id} hasOT={(s.overtime || s.scoring.some((p) => p.period === 4))} playerMap={playerMap} />
            )}

            {/* ============== Three Stars ============== */}
            {Array.isArray(s.three_stars) && s.three_stars.some((x) => x.player_id) && (
                <section className="rosterly-card p-5" data-testid="gc-three-stars">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4 flex items-center gap-2">
                        <Star size={14} className="text-[#FBBF24]" /> Three Stars
                    </h2>
                    <div className="grid md:grid-cols-3 gap-4">
                        {s.three_stars
                            .slice()
                            .sort((a, b) => a.star - b.star)
                            .filter((x) => x.player_id)
                            .map((x) => {
                                const p = playerMap[x.player_id];
                                const t = teamObj(x.team_id);
                                const statLine = starStatLine(x.player_id, skaterLogMap, goalieLogMap, p);
                                return (
                                    <Link
                                        key={x.star}
                                        to={p ? `/players/${x.player_id}` : "#"}
                                        className="border border-[#1E2233] rounded p-4 flex items-center gap-3 hover:border-[#9D4CDD] transition-colors"
                                        data-testid={`gc-star-${x.star}`}
                                    >
                                        <div className="relative shrink-0">
                                            <PlayerAvatar photo={p?.photo_url} name={p?.name || x.player_id} playerId={x.player_id} size="lg" />
                                            <span
                                                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center font-mono-stat text-[11px] font-black border-2 border-[#0A0B10]"
                                                style={{ background: t.color, color: "#fff" }}
                                            >
                                                {x.star}
                                            </span>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] uppercase tracking-wider text-[#FBBF24] font-bold">
                                                {x.star === 1 ? "1st Star" : x.star === 2 ? "2nd Star" : "3rd Star"}
                                            </div>
                                            <div className="font-bold text-white truncate">{p?.name || x.player_id}</div>
                                            <div className="text-xs text-[#94A3B8]">{t.abbreviation} · #{p?.jersey || "?"}</div>
                                            {statLine && (
                                                <div className="mt-1 inline-flex items-center gap-1 rounded bg-white/5 px-2 py-0.5 font-mono-stat text-[11px] text-[#E9D5FF]" data-testid={`gc-star-${x.star}-stats`}>
                                                    {statLine}
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                    </div>
                </section>
            )}

            {/* ============== Scoring ============== */}
            {Array.isArray(s.scoring) && s.scoring.length > 0 && (
                <section className="rosterly-card p-5" data-testid="gc-scoring-summary">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Scoring Summary</h2>
                    <ScoringTable plays={s.scoring} playerMap={playerMap} />
                </section>
            )}

            {/* ============== Penalties ============== */}
            {Array.isArray(s.penalties) && s.penalties.length > 0 && (
                <section className="rosterly-card p-5" data-testid="gc-penalties-summary">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Penalties</h2>
                    <PenaltyTable rows={s.penalties} playerMap={playerMap} />
                </section>
            )}

            {/* ============== Team stats ============== */}
            <section className="rosterly-card p-5" data-testid="gc-team-stats-summary">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Team Statistics</h2>
                <div className="flex items-center justify-between mb-3 text-xs font-bold uppercase tracking-wider" data-testid="gc-team-stats-legend">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: away.color }} />{away.abbreviation}</span>
                    <span className="inline-flex items-center gap-1.5">{home.abbreviation}<span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: home.color }} /></span>
                </div>
                <TeamStatsTable away={away} home={home} stats={{ away: s.away_team_stats, home: s.home_team_stats }} />
            </section>

            {/* ============== Goaltender summary ============== */}
            {Array.isArray(s.goalies) && s.goalies.length > 0 && (
                <section className="rosterly-card p-5" data-testid="gc-goalie-summary">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Goaltender Summary</h2>
                    <GoalieComparison rows={s.goalies} away={away} home={home} awayId={s.away_team_id} homeId={s.home_team_id} playerMap={playerMap} />
                    <GoalieSummaryTable rows={s.goalies} playerMap={playerMap} goalieLogMap={goalieLogMap} />
                </section>
            )}

            {/* ============== Skater game logs ============== */}
            <section className="rosterly-card p-5" data-testid="gc-skater-game-logs">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Skater Game Logs</h2>
                <SkaterLogsTable rows={skaterLogsQ.data || []} playerMap={playerMap} />
            </section>

            {/* ============== Goalie game logs ============== */}
            {(goalieLogsQ.data || []).length > 0 && (
                <section className="rosterly-card p-5" data-testid="gc-goalie-game-logs">
                    <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Goalie Game Logs</h2>
                    <GoalieLogsTable rows={goalieLogsQ.data} playerMap={playerMap} />
                </section>
            )}

            {/* Notes */}
            {s.notes && (
                <section className="rosterly-card p-5" data-testid="gc-notes">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Notes</h3>
                    <p className="text-sm text-[#94A3B8] whitespace-pre-wrap">{s.notes}</p>
                </section>
            )}

        </div>
    );
}

// ===== Sub-components =====

// Hockey-puck style scoreboard hero. Away colour glows on the left, home on the
// right; date + season up top, status pill, the matchup with logos/names/scores,
// and a PWHL mark at the bottom. For Scheduled games the centre shows the time.
function PuckScoreboard({ s, home, away, homeWon }) {
    const status = s.status || "Final";
    const scheduled = status.toLowerCase() === "scheduled";
    const centerLabel = scheduled && s.time ? s.time : status.toUpperCase();
    // Two-colour rim that wraps the whole puck: away on the left half, home on
    // the right half, meeting cleanly at the very top and bottom (no dark gaps).
    const rim = `conic-gradient(from 0deg, ${home.color} 0deg 180deg, ${away.color} 180deg 360deg)`;
    const screen = `radial-gradient(circle at 50% 46%, #0c0d13 0%, #06070e 64%), linear-gradient(100deg, ${away.color} -30%, transparent 42%, transparent 58%, ${home.color} 130%)`;
    return (
        <div className="flex justify-center" data-testid="gc-hero">
            <div className="relative w-full max-w-[600px] aspect-square select-none">
                {/* puck body */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-b from-[#17181f] to-[#050507] shadow-[0_40px_90px_-25px_rgba(0,0,0,0.9)]" />
                {/* colored rim — full ring */}
                <div className="absolute inset-[3%] rounded-full" style={{ background: rim }} />
                {/* soft seam blend so the two colours meet smoothly, not as a hard line */}
                <div className="absolute inset-[3%] rounded-full" style={{ background: rim, filter: "blur(6px)", opacity: 0.6 }} />
                {/* dark bevel ring (reads as the puck's edge) */}
                <div className="absolute inset-[6%] rounded-full bg-[#08090f] shadow-[inset_0_2px_10px_rgba(0,0,0,0.8)]" />
                {/* glossy top highlight */}
                <div className="absolute inset-[3%] rounded-full pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 38% at 50% 8%, rgba(255,255,255,0.16), transparent 70%)" }} />
                {/* inner screen */}
                <div
                    className="absolute inset-[7.5%] rounded-full overflow-hidden flex flex-col items-center justify-between text-center py-[10%] px-[9%]"
                    style={{ background: screen }}
                >
                    {/* top */}
                    <div className="flex flex-col items-center gap-1.5">
                        <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/85 font-bold">
                            {s.date ? formatDate(s.date, { fallback: "TBD" }) : "TBD"}
                        </div>
                        <div className="text-[8px] md:text-[10px] uppercase tracking-[0.2em] text-white/55 font-semibold">
                            {s.season} {s.stage} season
                        </div>
                        <span className="mt-1 px-3 py-0.5 rounded-md bg-white/10 text-white text-[9px] md:text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="gc-status-pill">{status}</span>
                    </div>
                    {/* matchup */}
                    <div className="grid grid-cols-3 items-start gap-1 w-full">
                        <TeamCol team={away} side="away" tid={s.away_team_id} score={s.away_score} dim={homeWon && !scheduled} />
                        <div className="flex flex-col items-center justify-center pt-6">
                            <div className="font-mono-stat text-base md:text-2xl font-black text-white/90 leading-tight">{centerLabel}</div>
                            {!scheduled && (s.overtime || s.shootout) && (
                                <div className="mt-1 text-[9px] uppercase tracking-widest text-[#FDE68A] font-bold">{s.shootout ? "SO" : "OT"}</div>
                            )}
                        </div>
                        <TeamCol team={home} side="home" tid={s.home_team_id} score={s.home_score} dim={!homeWon && !scheduled} />
                    </div>
                    {/* bottom */}
                    <div className="text-[10px] md:text-sm uppercase tracking-[0.4em] text-white/75 font-black">PWHL</div>
                </div>
            </div>
        </div>
    );
}

function TeamCol({ team, side, tid, score, dim }) {
    return (
        <Link to={`/teams/${tid}`} className="flex flex-col items-center gap-1.5 group min-w-0" data-testid={`gc-${side}-team`}>
            <span className="text-[8px] md:text-[9px] uppercase tracking-[0.25em] text-white/55 font-bold">{side}</span>
            <TeamBadge teamId={tid} size="lg" asLink={false} />
            <span className="font-heading text-sm md:text-2xl font-black uppercase leading-[0.95] text-white group-hover:text-white/80 transition-colors break-words">
                {team.name}
            </span>
            <span className={`font-mono-stat text-3xl md:text-6xl font-black leading-none ${dim ? "text-white/35" : "text-white"}`} data-testid={`gc-final-${side}`}>
                {score}
            </span>
        </Link>
    );
}

function ScoringTable({ plays, playerMap }) {
    return (
        <table className="rosterly-table w-full" data-testid="gc-scoring-table">
            <thead>
                <tr>
                    <th>Per</th><th>Time</th><th>Team</th><th>Strength</th><th>Goal</th><th>Assists</th>
                </tr>
            </thead>
            <tbody>
                {plays
                    .slice()
                    .sort((a, b) => a.period - b.period || String(a.time).localeCompare(String(b.time)))
                    .map((p, i) => (
                        <tr key={i} data-testid={`gc-score-row-${i}`}>
                            <td className="font-mono-stat">{periodLabel(p.period)}</td>
                            <td className="font-mono-stat text-xs">{p.time || "—"}</td>
                            <td><TeamBadge teamId={p.team_id} size="sm" /></td>
                            <td className="text-xs">
                                {STRENGTH_LABEL[p.strength] ? (
                                    <span className="pill bg-[#9D4CDD]/25 text-[#E9D5FF]">{STRENGTH_LABEL[p.strength]}</span>
                                ) : (
                                    <span className="text-[#64748B]">EV</span>
                                )}
                            </td>
                            <td className="stat-emph">
                                <PlayerLink pid={p.scorer_id} playerMap={playerMap} withAvatar />
                            </td>
                            <td className="text-sm">
                                {p.assist1_id || p.assist2_id ? (
                                    <span>
                                        <PlayerLink pid={p.assist1_id} playerMap={playerMap} />
                                        {p.assist2_id && <>, <PlayerLink pid={p.assist2_id} playerMap={playerMap} /></>}
                                    </span>
                                ) : (
                                    <span className="text-[#64748B] italic">unassisted</span>
                                )}
                            </td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );
}

function PenaltyTable({ rows, playerMap }) {
    return (
        <table className="rosterly-table w-full" data-testid="gc-penalty-table">
            <thead>
                <tr><th>Per</th><th>Time</th><th>Team</th><th>Player</th><th>Infraction</th><th>Min</th></tr>
            </thead>
            <tbody>
                {rows
                    .slice()
                    .sort((a, b) => a.period - b.period || String(a.time).localeCompare(String(b.time)))
                    .map((r, i) => (
                        <tr key={i} data-testid={`gc-penalty-row-${i}`}>
                            <td className="font-mono-stat">{periodLabel(r.period)}</td>
                            <td className="font-mono-stat text-xs">{r.time || "—"}</td>
                            <td><TeamBadge teamId={r.team_id} size="sm" /></td>
                            <td><PlayerLink pid={r.player_id} playerMap={playerMap} withAvatar /></td>
                            <td className="text-sm">{r.infraction || "—"}{r.note ? <span className="text-[#64748B]"> · {r.note}</span> : ""}</td>
                            <td className="font-mono-stat">{r.minutes}</td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );
}

function TeamStatsTable({ away, home, stats }) {
    const aS = stats.away || {};
    const hS = stats.home || {};
    // Each row: display string per side + a numeric value used to size the
    // reactive comparison bar (away segment vs home segment).
    const foPct = (s) => {
        const w = s.fo_won ?? 0, l = s.fo_lost ?? 0;
        return (w + l) ? (w / (w + l)) * 100 : 0;
    };
    const rows = [
        {
            label: "Faceoffs", tid: "faceoffs",
            disp: (s) => { const w = s.fo_won ?? 0, l = s.fo_lost ?? 0; return (w + l) ? `${foPct(s).toFixed(1)}%` : "—"; },
            num: foPct,
        },
        {
            label: "Power Play", tid: "power play",
            disp: (s) => { const g = s.pp_goals, a = s.pp_attempts; return (g == null && a == null) ? "—" : `${g ?? 0}/${a ?? 0}`; },
            num: (s) => s.pp_goals ?? 0,
        },
        { label: "Hits", tid: "hits", disp: (s) => (s.hits == null || s.hits === "") ? "—" : s.hits, num: (s) => Number(s.hits) || 0 },
        { label: "Shots", tid: "shots", disp: (s) => (s.shots == null || s.shots === "") ? "—" : s.shots, num: (s) => Number(s.shots) || 0 },
    ];
    return (
        <div className="space-y-4" data-testid="gc-team-stats-table">
            {rows.map((row, i) => {
                const aNum = row.num(aS);
                const hNum = row.num(hS);
                const total = aNum + hNum;
                const aPct = total > 0 ? (aNum / total) * 100 : 50;
                const hPct = total > 0 ? (hNum / total) * 100 : 50;
                const aLead = aNum > hNum, hLead = hNum > aNum;
                return (
                    <div key={row.label} data-testid={`gc-ts-row-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                            <span className={`font-mono-stat tabular-nums ${aLead ? "text-white font-bold" : "text-[#94A3B8]"}`} data-testid={`gc-ts-away-${row.tid}`}>{row.disp(aS)}</span>
                            <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">{row.label}</span>
                            <span className={`font-mono-stat tabular-nums ${hLead ? "text-white font-bold" : "text-[#94A3B8]"}`} data-testid={`gc-ts-home-${row.tid}`}>{row.disp(hS)}</span>
                        </div>
                        <div className="relative flex h-3 rounded-full overflow-hidden bg-[#1E2233] shadow-inner" data-testid={`gc-ts-bar-${i}`}>
                            <div
                                className="h-full transition-all duration-500 ease-out"
                                style={{ width: `${aPct}%`, background: `linear-gradient(90deg, ${away.color}99, ${away.color})`, opacity: aLead ? 1 : 0.6 }}
                                data-testid={`gc-ts-bar-away-${i}`}
                                title={`${away.abbreviation}: ${row.disp(aS)}`}
                            />
                            <div
                                className="h-full transition-all duration-500 ease-out"
                                style={{ width: `${hPct}%`, background: `linear-gradient(90deg, ${home.color}, ${home.color}99)`, opacity: hLead ? 1 : 0.6 }}
                                data-testid={`gc-ts-bar-home-${i}`}
                                title={`${home.abbreviation}: ${row.disp(hS)}`}
                            />
                            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/30" />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Goalie MIN is the SAME thing as TOI, just a different column title. Always
// render it MM:SS: pass through real TOI strings; format an integer "minutes"
// (total minutes played) as MM:00.
const fmtGoalieMin = (v) => {
    if (v == null || v === "" || v === 0) return "—";
    if (typeof v === "string") return v.includes(":") ? v : (Number(v) ? `${Number(v)}:00` : "—");
    return `${Math.round(v)}:00`;
};

function GoalieSummaryTable({ rows, playerMap, goalieLogMap = {} }) {
    // Spec columns (all must appear). Blue headers = per-period/game-stage stats
    // (per-period goalie splits aren't captured yet, so these show game totals).
    const val = (r) => {
        const log = goalieLogMap[r.player_id] || {};
        const sa = log.shots_against ?? r.shots_against ?? 0;
        const sv = log.saves ?? r.saves ?? 0;
        const ga = log.goals_against ?? r.goals_against ?? 0;
        const g = log.g ?? 0, a = log.a ?? 0;
        const soa = log.soa ?? 0, soga = log.soga ?? 0;
        const soPct = soa > 0 ? `${Math.round(((soa - soga) / soa) * 100)}%` : "—";
        return {
            sa, sv, ga, g, a, p: g + a, pim: log.pim ?? 0,
            min: fmtGoalieMin(log.toi || r.toi || log.minutes),
            so: (log.shutout ?? r.shutout) ? "✓" : "",
            result: log.decision || r.decision || "—",
            soLine: (soa || soga) ? `${soa}/${soga}` : "—",
            soPct,
        };
    };
    const BLUE = "text-[#38BDF8]";
    return (
        <div className="overflow-x-auto">
            <table className="rosterly-table w-full" data-testid="gc-goalie-summary-table">
                <thead>
                    <tr>
                        <th>Team</th><th>Goalie</th>
                        <th className={`text-right ${BLUE}`}>SV/SA</th>
                        <th className={`text-right ${BLUE}`}>SV%</th>
                        <th className={`text-right ${BLUE}`}>Min</th>
                        <th className={`text-right ${BLUE}`}>GA</th>
                        <th className={`text-right ${BLUE}`}>P</th>
                        <th className={`text-right ${BLUE}`}>G</th>
                        <th className={`text-right ${BLUE}`}>A</th>
                        <th className={`text-right ${BLUE}`}>PIM</th>
                        <th className={`text-right ${BLUE}`}>SO</th>
                        <th className="text-right">Result</th>
                        <th className="text-right">SOA/SOGA</th>
                        <th className="text-right">SO%</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => {
                        const v = val(r);
                        return (
                            <tr key={i} data-testid={`gc-goalie-summary-row-${i}`}>
                                <td><TeamBadge teamId={r.team_id} size="sm" /></td>
                                <td className="stat-emph"><PlayerLink pid={r.player_id} playerMap={playerMap} /></td>
                                <td className="text-right font-mono-stat">{v.sv}/{v.sa}</td>
                                <td className="text-right font-mono-stat">{fmtSv(v.sa, v.sv)}</td>
                                <td className="text-right font-mono-stat text-xs">{v.min}</td>
                                <td className="text-right font-mono-stat">{v.ga}</td>
                                <td className="text-right font-mono-stat font-bold">{v.p}</td>
                                <td className="text-right font-mono-stat">{v.g}</td>
                                <td className="text-right font-mono-stat">{v.a}</td>
                                <td className="text-right font-mono-stat">{v.pim}</td>
                                <td className="text-right font-mono-stat">{v.so}</td>
                                <td className="text-right font-mono-stat">{v.result}</td>
                                <td className="text-right font-mono-stat text-xs">{v.soLine}</td>
                                <td className="text-right font-mono-stat">{v.soPct}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="mt-2 text-[10px] text-[#38BDF8]/70 italic">Blue columns are per-period / game-stage stats — currently showing game totals (per-period goalie splits aren't captured yet).</p>
        </div>
    );
}

function SkaterLogsTable({ rows, playerMap }) {
    if (rows.length === 0) {
        return <p className="text-sm text-[#64748B] italic">No skater logs entered yet.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="rosterly-table w-full" data-testid="gc-skater-logs-table">
                <thead>
                    <tr>
                        <th>Team</th><th>Player</th>
                        <th className="text-right">TOI</th>
                        <th className="text-right">G</th><th className="text-right">A</th><th className="text-right">P</th>
                        <th className="text-right">+/-</th>
                        <th className="text-right">SOG</th><th className="text-right">Hits</th><th className="text-right">Blk</th>
                        <th className="text-right">FO W-L</th><th className="text-right">PIM</th>
                        <th className="text-right">PPG</th><th className="text-right">PPA</th>
                        <th className="text-right">SHG</th><th className="text-right">GWG</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} data-testid={`gc-skater-log-row-${r.player_id}`}>
                            <td><TeamBadge teamId={r.team_id} size="sm" /></td>
                            <td className="stat-emph"><PlayerLink pid={r.player_id} playerMap={playerMap} /></td>
                            <td className="text-right font-mono-stat text-xs">{r.toi || "—"}</td>
                            <td className="text-right font-mono-stat">{r.g}</td>
                            <td className="text-right font-mono-stat">{r.a}</td>
                            <td className="text-right font-mono-stat font-bold">{r.p}</td>
                            <td className="text-right font-mono-stat">{r.plus_minus > 0 ? `+${r.plus_minus}` : r.plus_minus}</td>
                            <td className="text-right font-mono-stat">{r.sog}</td>
                            <td className="text-right font-mono-stat">{r.hits}</td>
                            <td className="text-right font-mono-stat">{r.blocks}</td>
                            <td className="text-right font-mono-stat text-xs">{r.fow}-{r.fol}</td>
                            <td className="text-right font-mono-stat">{r.pim}</td>
                            <td className="text-right font-mono-stat">{r.ppg}</td>
                            <td className="text-right font-mono-stat">{r.ppa}</td>
                            <td className="text-right font-mono-stat">{r.shg}</td>
                            <td className="text-right font-mono-stat">{r.gwg}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function GoalieLogsTable({ rows, playerMap }) {
    return (
        <div className="overflow-x-auto">
            <table className="rosterly-table w-full" data-testid="gc-goalie-logs-table">
                <thead>
                    <tr>
                        <th>Team</th><th>Goalie</th>
                        <th>Dec</th><th>Start</th><th className="text-right">TOI</th>
                        <th className="text-right">SA</th><th className="text-right">GA</th><th className="text-right">SV</th><th className="text-right">SV%</th><th>SO</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} data-testid={`gc-goalie-log-row-${r.player_id}`}>
                            <td><TeamBadge teamId={r.team_id} size="sm" /></td>
                            <td className="stat-emph"><PlayerLink pid={r.player_id} playerMap={playerMap} /></td>
                            <td className="font-mono-stat">{r.decision || "—"}</td>
                            <td>{r.starter ? "✓" : ""}</td>
                            <td className="text-right font-mono-stat text-xs">{r.toi || "—"}</td>
                            <td className="text-right font-mono-stat">{r.shots_against}</td>
                            <td className="text-right font-mono-stat">{r.goals_against}</td>
                            <td className="text-right font-mono-stat">{r.saves}</td>
                            <td className="text-right font-mono-stat">{fmtSv(r.shots_against, r.saves)}</td>
                            <td>{r.shutout ? "✓" : ""}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ===== Game-box sub-components (Phase 2) =====

// Compact stat line for a Three-Star, derived from this game's logs.
function starStatLine(pid, skaterLogMap, goalieLogMap, p) {
    const gl = goalieLogMap[pid];
    if (gl || p?.position === "G") {
        if (!gl) return null;
        return `${fmtSv(gl.shots_against, gl.saves)} SV% · ${gl.saves ?? 0} SV · ${gl.goals_against ?? 0} GA${gl.shutout ? " · SO" : ""}`;
    }
    const sl = skaterLogMap[pid];
    if (!sl) return null;
    const parts = [];
    if ((sl.g ?? 0) > 0) parts.push(`${sl.g}G`);
    if ((sl.a ?? 0) > 0) parts.push(`${sl.a}A`);
    const pts = sl.p ?? ((sl.g ?? 0) + (sl.a ?? 0));
    return `${parts.join(" ") || "0G 0A"} · ${pts} PTS`;
}

// Venue / attendance / external link strip below the scoreboard.
function GameInfoStrip({ s }) {
    const hasVenue = !!s.venue;
    const hasAtt = (s.attendance || 0) > 0;
    const hasUrl = !!s.game_url;
    if (!hasVenue && !hasAtt && !hasUrl) return null;
    return (
        <div className="rosterly-card p-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm" data-testid="gc-info-strip">
            {hasVenue && (
                <span className="inline-flex items-center gap-2 text-[#94A3B8]" data-testid="gc-info-venue">
                    <MapPin size={15} className="text-[#9D4CDD]" />
                    <span className="text-white font-semibold">{s.venue}</span>
                </span>
            )}
            {hasAtt && (
                <span className="inline-flex items-center gap-2 text-[#94A3B8]" data-testid="gc-info-attendance">
                    <Users size={15} className="text-[#9D4CDD]" />
                    Attendance <span className="text-white font-mono-stat font-bold tabular-nums">{Number(s.attendance).toLocaleString()}</span>
                </span>
            )}
            {hasUrl && (
                <a href={s.game_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#9D4CDD] hover:text-white font-bold uppercase text-xs tracking-wider transition-colors" data-testid="gc-info-link">
                    <ExternalLink size={14} /> Game Page
                </a>
            )}
        </div>
    );
}

const STORY_ICON = { flame: Flame, snowflake: Snowflake, star: Star, zap: Zap, target: Target };
const STORY_TONE = {
    good: { ring: "border-emerald-500/40", glow: "bg-emerald-500/10", ic: "text-emerald-400" },
    bad: { ring: "border-red-500/40", glow: "bg-red-500/10", ic: "text-red-400" },
    watch: { ring: "border-amber-500/40", glow: "bg-amber-500/10", ic: "text-amber-400" },
};

function StorylineCards({ cards }) {
    return (
        <section className="space-y-4" data-testid="gc-storylines">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] flex items-center gap-2">
                <Activity size={14} className="text-[#9D4CDD]" /> Storylines
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cards.map((c, i) => {
                    const Icon = STORY_ICON[c.icon] || Activity;
                    const tone = STORY_TONE[c.tone] || STORY_TONE.watch;
                    return (
                        <div key={i} className={`rosterly-card p-4 flex items-start gap-3 border ${tone.ring}`} data-testid={`gc-storyline-${i}`}>
                            <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${tone.glow}`}>
                                <Icon size={18} className={tone.ic} />
                            </div>
                            <div className="min-w-0">
                                <div className="font-bold text-white text-sm leading-tight">{c.title}</div>
                                <div className="text-xs text-[#94A3B8] mt-0.5">{c.text}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

// Horizontal goal timeline: one track per period, goal dots placed by time,
// coloured by the scoring team. 1st ─── 2nd ─── 3rd (─── OT).
function GameTimeline({ scoring, home, away, homeId, awayId, hasOT, playerMap }) {
    const periods = [1, 2, 3];
    if (hasOT) periods.push(4);
    const PERIOD_SECS = 1200;
    const toSecs = (t) => {
        const [m, sec] = String(t || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
        return m * 60 + sec;
    };
    const byPeriod = {};
    scoring.filter((p) => p.period <= 4).forEach((p) => { (byPeriod[p.period] ||= []).push(p); });
    return (
        <section className="rosterly-card p-5" data-testid="gc-timeline">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#94A3B8] mb-4">Goal Timeline</h2>
            <div className="flex items-stretch gap-2">
                {periods.map((per) => (
                    <div key={per} className="flex-1 min-w-0">
                        <div className="text-center text-[10px] uppercase tracking-wider text-[#64748B] font-bold mb-1.5">{periodLabel(per)}</div>
                        <div className="relative h-12 rounded-md bg-[#0E1018] border border-[#1E2233] overflow-hidden">
                            <div className="absolute top-1/2 left-0 right-0 h-px bg-[#1E2233]" />
                            {(byPeriod[per] || []).map((g, i) => {
                                const t = teamObj(g.team_id);
                                const isHome = g.team_id === homeId;
                                const left = Math.min(94, Math.max(6, (toSecs(g.time) / PERIOD_SECS) * 100));
                                const scorer = playerMap[g.scorer_id]?.name || "Goal";
                                return (
                                    <div
                                        key={i}
                                        className="absolute -translate-x-1/2 group"
                                        style={{ left: `${left}%`, top: isHome ? "58%" : "10%" }}
                                        data-testid={`gc-timeline-goal-${per}-${i}`}
                                    >
                                        <span className="block w-3.5 h-3.5 rounded-full border-2 border-[#0A0B10] shadow-lg transition-transform group-hover:scale-150" style={{ background: t.color }} />
                                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded bg-black/90 px-2 py-1 text-[10px] text-white z-10">
                                            {t.abbreviation} · {g.time || "—"} · {scorer}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-3 flex items-center justify-center gap-5 text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: away.color }} />{away.abbreviation}</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: home.color }} />{home.abbreviation}</span>
            </div>
        </section>
    );
}

// Side-by-side starting-goalie comparison with a SV% face-off bar.
function GoalieComparison({ rows, away, home, awayId, homeId, playerMap }) {
    const pick = (tid) => rows.filter((r) => r.team_id === tid).sort((a, b) => (b.shots_against || 0) - (a.shots_against || 0))[0];
    const ag = pick(awayId), hg = pick(homeId);
    if (!ag || !hg) return null;
    const svNum = (g) => (g.shots_against ? g.saves / g.shots_against : 0);
    const aSv = svNum(ag), hSv = svNum(hg);
    const total = aSv + hSv;
    const aPct = total ? (aSv / total) * 100 : 50;
    const Side = ({ g, team, align }) => {
        const p = playerMap[g.player_id];
        return (
            <div className={`flex-1 ${align === "right" ? "text-right" : ""}`} data-testid={`gc-goalie-cmp-${align === "right" ? "home" : "away"}`}>
                <div className={`flex items-center gap-2 ${align === "right" ? "flex-row-reverse" : ""}`}>
                    <PlayerAvatar photo={p?.photo_url} name={p?.name || g.player_id} playerId={g.player_id} size="md" showTierRing={false} />
                    <div className={align === "right" ? "text-right" : ""}>
                        <div className="font-bold text-white text-sm leading-tight">{p?.name || g.player_id}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">{team.abbreviation}{g.decision ? ` · ${g.decision}` : ""}</div>
                    </div>
                </div>
                <div className={`mt-2 font-mono-stat text-2xl font-black text-white tabular-nums`}>{fmtSv(g.shots_against, g.saves)}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-mono-stat">{g.saves}/{g.shots_against} SV · {g.goals_against} GA</div>
            </div>
        );
    };
    return (
        <div className="mb-5 rounded-lg border border-[#1E2233] p-4 bg-[#0E1018]" data-testid="gc-goalie-comparison">
            <div className="flex items-start gap-3">
                <Side g={ag} team={away} align="left" />
                <span className="self-center text-[10px] uppercase tracking-widest text-[#64748B] font-bold px-2">SV%</span>
                <Side g={hg} team={home} align="right" />
            </div>
            <div className="mt-3 flex h-2.5 rounded-full overflow-hidden bg-[#1E2233]">
                <div className="h-full transition-all duration-500" style={{ width: `${aPct}%`, background: away.color }} />
                <div className="h-full transition-all duration-500" style={{ width: `${100 - aPct}%`, background: home.color }} />
            </div>
        </div>
    );
}

function PlayerLink({ pid, playerMap, withAvatar = false }) {
    if (!pid) return <span className="text-[#64748B]">—</span>;
    const p = playerMap[pid];
    const inner = withAvatar ? (
        <span className="inline-flex items-center gap-2">
            <PlayerAvatar photo={p?.photo_url} name={p?.name || pid} playerId={pid} size="xs" showTierRing={false} />
            <span>{p?.name || pid}</span>
        </span>
    ) : (p?.name || pid);
    if (!p) return <span>{inner}</span>;
    return (
        <Link to={`/players/${pid}`} className="hover:text-[#9D4CDD] transition-colors" data-testid={`gc-player-link-${pid}`}>
            {inner}
        </Link>
    );
}
